<template>
  <content-popup
    class="dictionary-popup"
    :visible="showPopup"
    :reference-element="refElement"
    :boundary="popupBoundary"
    :root-boundary="popupRootBoundary"
    :additional-scrolling-offset="additionalScrollingOffset"
    :primary-button-label="t('dictionary_popup_add_to_notes_label')"
    :secondary-button-label="t('dictionary_popup_close_label')"
    :show-cancel-button="true"
    :disable-save-button="disableAddToNoteButton"
    @on-confirm="handleConfirmClick"
    @on-cancel="handleCancel"
  >
    <template #content>
      <dictionary-term
        ref="dictionaryTerm"
        :term="term"
        :dictionary-language="popupData.dictionaryLanguage"
        :dictionary-settings="popupData.dictionarySettings"
        :search-term="searchTerm"
        :context="context"
        :confirm-add-to-note="confirmAddToNote"
        @term-searched="onTermSearched"
        @on-confirm-add-to-note="handleAddToNotes"
      />
      <cv-inline-notification
        v-if="hasError"
        low-contrast
        kind="error"
        :title="error.message"
        @close="handleCloseError"
      >
        <template #subtitle>
          <div v-safe-html="error.subMessage" />
        </template>
      </cv-inline-notification>
    </template>
  </content-popup>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, ref, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";
import DOMUtils from "@utils/dom.utils";
import DictionaryTerm from "./DictionaryTerm.vue";
import { getValueOrDefault, isDefined } from "@utils/var.check";
import { useErrorHandler } from "@/plugins/common/composables/error";

interface PopupData {
  highlightData?: unknown;
  dictionaryLanguage?: string;
  dictionarySettings?: unknown;
  canAddToNote?: boolean;
  [key: string]: unknown;
}

interface Props {
  visible: boolean;
  // has value "highlight" or "search"
  // "search": for normal search based on input on popup
  // "highlight": for term highlighted from annotation
  context: string;
  popupData?: PopupData;
}

const props = withDefaults(defineProps<Props>(), {
  popupData: () => ({}),
});

const emit = defineEmits<{
  popupClosed: [];
  addToNote: [data: unknown];
}>();

const { t } = useI18n();
const store = useStore();
const { error, hasError, handleCloseError, clearErrorState } = useErrorHandler();

const dictionaryTermRef = useTemplateRef("dictionaryTerm");
const popupBoundary = ref(".lesson-viewer-activity-component");
const popupRootBoundary = ref(".page-main");
const additionalScrollingOffset = ref(0);
const refElement = ref<HTMLElement | null>(null);
const highlightedId = ref("");
const highlighter = ref<unknown>("");
const showPopup = ref(false);
const term = ref("");
const searchTerm = ref(false);
const existingHighlight = ref(false);
const hasSearchResult = ref(false);
const confirmAddToNote = ref(false);
const hasSearchAction = ref(false);
const hasHitEnter = ref(false);

// const unwatchAnnotationPopupState = ref<(() => void) | null>(null);

const hasHighlightContext = computed(() => props.context === "highlight");

const disableAddToNoteButton = computed(
  () => !hasSearchResult.value || !props.popupData.canAddToNote,
);

const setWatchStoreData = () => {
  // Close dictionary popup when annotation popup is opened
  store.watch(
    (state) => (state.annotation ? state.annotation.annotationPopupState : ""),
    (value) => {
      if (value) {
        showPopup.value = false;
        setDefaultState();
        dictionaryTermRef.value?.setDefaultData();
        emit("popupClosed");
      }
    },
  );
};

const handleOpenPopup = () => {
  if (hasHighlightContext.value) {
    //To prevent other TextAnnotation components to come in as well
    if (!isDefined(props.popupData.highlightData)) return;
    setData();
  }
  showPopup.value = true;
};

const handleCancel = () => {
  if (hasHighlightContext.value) highlighter.value.removeHighlight(highlightedId.value);
  showPopup.value = false;
  setDefaultState();
  dictionaryTermRef.value?.setDefaultData();
  emit("popupClosed");
};

const handleConfirmClick = () => {
  confirmAddToNote.value = true;
};

const handleAddToNotes = (content) => {
  if (disableAddToNoteButton.value) return;
  confirmAddToNote.value = false;
  const data = {
    content: {
      definitions: content.definition,
      pronunciation: content.pronunciation,
    },
    term: content.term,
    highlightedId: highlightedId.value,
    context: props.context,
  };
  emit("addToNote", data);
};

const handleNotesSavedSuccessfully = () => {
  showPopup.value = false;
  setDefaultState();
  dictionaryTermRef.value?.setDefaultData();
};

const onTermSearched = (data) => {
  hasHitEnter.value = data.hasHitEnter;
  hasSearchResult.value = data.hasSearchResult;
  hasSearchAction.value = data.hasSearchAction;
};

const setDefaultState = () => {
  additionalScrollingOffset.value = 0;
  refElement.value = null;
  highlightedId.value = "";
  highlighter.value = "";
  term.value = "";
  searchTerm.value = false;
  existingHighlight.value = false;
  hasSearchResult.value = false;
};

const setData = () => {
  const data = props.popupData.highlightData;
  const parentElement = data.parentElement;
  term.value = data.highlightedContent.trim();
  searchTerm.value = true;
  highlighter.value = data.highlighter;
  highlightedId.value = data.highlighterId;
  refElement.value = getValueOrDefault(getReferenceContainer(parentElement), null);
  if (refElement.value) {
    additionalScrollingOffset.value = getValueOrDefault(refElement.value.offsetHeight, 0);
  } else {
    additionalScrollingOffset.value = 0;
  }
  const containerParent = DOMUtils.parent(refElement.value, ".container-component");
  if (
    containerParent &&
    (containerParent.classList.contains("two-column") ||
      containerParent.classList.contains("three-column"))
  ) {
    popupBoundary.value = ".container-component";
  } else if (containerParent && containerParent.classList.contains("quiz-page-component")) {
    popupBoundary.value = ".quiz-page-component";
  } else {
    popupBoundary.value = ".lesson-viewer-activity-component";
  }
  clearErrorState();
};

const getReferenceContainer = (parentElement) => {
  // Existing highlight exists, returning the original highlight element instead
  if (document.querySelector(`[data-id="${highlightedId.value}"]`) === null) {
    existingHighlight.value = true;
    return parentElement;
  }
  return document.querySelector(`[data-id="${highlightedId.value}"]`);
};

watch(
  () => props.visible,
  (value) => {
    if (value) {
      handleOpenPopup();
    }
  },
);

watch(hasSearchAction, (value) => {
  if (value && !hasHitEnter.value) {
    if (hasHighlightContext.value) {
      highlighter.value.removeHighlight(highlightedId.value);
    }
  }
});

onBeforeMount(() => {
  setWatchStoreData();
});

defineExpose({
  handleNotesSavedSuccessfully,
});
</script>

<style lang="scss">
@import "@styles/abstracts/variables";
@import "@styles/layout/breakpoint";

.dictionary-popup {
  &.content-popup-container {
    inset: unset !important;
    transform: unset !important;
    div.content-popup {
      @include media("desktop") {
        max-width: $comfort-reading-width;
      }
    }
  }
}
</style>
